import { FileDown } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { downloadPdf } from "@/lib/pdf";
import { formatCurrency } from "@/lib/utils";
import { expenseService } from "@/services/expense";

import { ExpensePdfDocument } from "./expense-pdf-document";
import { formatPeriod } from "./format";

import type { Expense } from "@artisancode/api-types";

const sumAmount = (rows: Expense[]) => rows.reduce((s, e) => s + e.amount, 0);

export function ExpensePdfButton({
  dateFrom,
  dateTo,
}: {
  dateFrom: string;
  dateTo: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [preview, setPreview] = useState<{
    done: Expense[];
    drafts: Expense[];
  } | null>(null);

  // Fetch every status so the confirmation can say what is left out of the PDF.
  async function openPreview() {
    setIsLoading(true);
    try {
      const items: Expense[] = [];
      let page = 1;
      let lastPage = 1;
      do {
        const res = await expenseService.list({
          date_from: dateFrom,
          date_to: dateTo,
          per_page: 100,
          page,
        });
        items.push(...res.items);
        lastPage = res.pagination.lastPage;
        page += 1;
      } while (page <= lastPage);
      setPreview({
        done: items.filter((e) => e.status === "done"),
        drafts: items.filter((e) => e.status === "draft"),
      });
    } catch {
      toast.error("Gagal memuat data pengeluaran.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDownload() {
    if (!preview) return;
    setIsGenerating(true);
    try {
      await downloadPdf(
        <ExpensePdfDocument
          expenses={preview.done}
          periodStart={dateFrom}
          periodEnd={dateTo}
        />,
        `Pengeluaran-${dateFrom}-${dateTo}.pdf`,
      );
      setPreview(null);
    } catch {
      toast.error("Gagal membuat PDF pengeluaran.");
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={openPreview}
        disabled={isLoading}
      >
        <FileDown className="mr-1.5 h-4 w-4" />
        {isLoading ? "Memuat..." : "Unduh PDF"}
      </Button>

      <ConfirmDialog
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
        title="Unduh PDF Pengeluaran"
        description={`Periode ${formatPeriod(dateFrom, dateTo)}. Hanya rentang tanggal yang dipakai; filter lain di halaman diabaikan.`}
        confirmLabel="Unduh PDF"
        pendingLabel="Membuat PDF..."
        variant="default"
        onConfirm={handleDownload}
        isConfirming={isGenerating}
        confirmDisabled={!preview?.done.length}
      >
        {preview && (
          <ul className="space-y-2">
            <li className="rounded-md border p-2">
              <p className="font-medium">
                Masuk PDF: {preview.done.length} pengeluaran selesai
              </p>
              <p className="text-muted-foreground">
                Total {formatCurrency(sumAmount(preview.done))}
              </p>
            </li>
            {preview.drafts.length > 0 && (
              <li className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
                <p className="font-medium">
                  Tidak ikut: {preview.drafts.length} draft (
                  {formatCurrency(sumAmount(preview.drafts))})
                </p>
                <p className="text-xs">
                  Selesaikan (acc) dulu supaya masuk PDF.
                </p>
              </li>
            )}
            {preview.done.length === 0 && (
              <li className="text-xs text-muted-foreground">
                Belum ada pengeluaran selesai di periode ini, PDF belum bisa
                dibuat.
              </li>
            )}
          </ul>
        )}
      </ConfirmDialog>
    </>
  );
}
